import React, { useMemo } from 'react';

import Link from 'next/link';

import analytics from '@repo/analytics';
import { MERCHANDISE_SLUG } from '@repo/editor/src/components/ElementPanels/TemplatesPanel/constants';
import { TransformedUseCase } from '@repo/editor/src/types';
import { withEventScope } from '@repo/event-processor';
import { IconSwitcher } from '@repo/ui/components/Icon';
import { P3 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import { USECASES_CATEGORY } from 'global/analyticsEvents';
import { OPENED_AI_TOOLBAR_EDITOR_URL } from 'global/urlUtils';
import { ALL_SLUG } from 'global/utils/templateParams';
import {
  useTemplatesCategory,
  useTemplatesCategoryUrl,
} from 'global/utils/useTemplatesParams';

import { filterCategories } from '../utils';

import { KITTL_AI_SLUG, USECASE_ICONS } from './config';
import { IconWrapper, RoundButton, Wrapper } from './styles';
import { CategorySectionProps } from './type';

const RoundButtonComponent: React.FC<{
  onClick?: () => void;
  icon: React.ReactNode;
  text: string;
  isActive?: boolean;
}> = ({ onClick, icon, text, isActive }) => (
  <RoundButton onClick={onClick}>
    <IconWrapper isActive={isActive}>{icon}</IconWrapper>
    <P3 color="white">{text}</P3>
  </RoundButton>
);

const CategorySection: React.FC<CategorySectionProps> = ({ allUseCases }) => {
  const currentCategorySlug = useTemplatesCategory();
  const buildCategoryUrl = useTemplatesCategoryUrl();

  const allUseCasesFiltered = useMemo((): TransformedUseCase[] => {
    const useCasesFiltered = filterCategories(allUseCases).sort((a, b) => {
      // prioritize Merchandise
      if (a.slug === MERCHANDISE_SLUG) {
        return -1;
      }
      if (b.slug === MERCHANDISE_SLUG) {
        return 1;
      }
      return 0;
    });

    return [
      { name: 'Discover', slug: ALL_SLUG, total: 0, popularity: 0 },
      ...useCasesFiltered,
      { name: 'Kittl AI', slug: KITTL_AI_SLUG, total: 0, popularity: 0 },
    ];
  }, [allUseCases]);

  const handleUseCaseClick = ({
    name,
    slug,
  }: Pick<TransformedUseCase, 'name' | 'slug'>): void => {
    void analytics.track(USECASES_CATEGORY, { name, slug });
  };

  return (
    <Wrapper>
      {allUseCasesFiltered?.map((useCase) => {
        const isActive = useCase.slug === currentCategorySlug;
        return (
          <Link
            key={useCase.slug}
            data-testid={`usecases-category--${useCase.slug}`}
            href={
              useCase.slug === KITTL_AI_SLUG
                ? OPENED_AI_TOOLBAR_EDITOR_URL
                : buildCategoryUrl(useCase)
            }
            rel="nofollow"
            target={useCase.slug === KITTL_AI_SLUG ? '_blank' : '_self'}
            onClick={(): void => handleUseCaseClick(useCase)}
          >
            <RoundButtonComponent
              icon={
                <IconSwitcher
                  icon={USECASE_ICONS[useCase.slug]}
                  theme={{
                    color: isActive
                      ? themeStyle.inkMain
                      : themeStyle.backgroundAlt,
                  }}
                />
              }
              isActive={isActive}
              text={useCase.name}
            />
          </Link>
        );
      })}
    </Wrapper>
  );
};

export default withEventScope(CategorySection, { feature: 'designsCategory' });
